import { ChevronDown, PlusCircle } from '@tamagui/lucide-icons';
import { Row } from '@unistocks/components/layout/flex';
import { Spinner } from '@unistocks/components/loading/spinner/spinner';
import { UnistocksCircle } from '@unistocks/components/logo/unistocks-circle';
import { Page } from '@unistocks/components/page/page';
import { Select } from '@unistocks/components/select/select';
import { selectSelectedWatchlistId } from '@unistocks/features/watchlists/store/watchlist.slice';
import { t } from '@unistocks/localization';
import { ListResultDtoOfLookupDtoOfGuid } from '@unistocks/proxy/axios-client';
import { useWatchlistsGetListQuery } from '@unistocks/proxy/axios-client/WatchlistsQuery';
import { useAppSelector } from '@unistocks/shared/hooks/redux-hooks';
import { SelectOption } from '@unistocks/types';
import { useRouter } from 'expo-router';
import { PropsWithChildren, memo, useCallback } from 'react';
import { isWeb } from 'tamagui';

export const WatchlistHeaderTitle = memo<PropsWithChildren>(({ children }) => {
  const router = useRouter();
  const selected = useAppSelector(selectSelectedWatchlistId) ?? '';
  const {
    data: portfolios,
    isLoading,
    isSuccess
  } = useWatchlistsGetListQuery({
    structuralSharing: false,
    select: useCallback(
      ({ items }: ListResultDtoOfLookupDtoOfGuid) =>
        items
          .map(
            ({ id, displayName }) =>
              ({ key: displayName, value: id, icon: <UnistocksCircle /> }) as SelectOption
          )
          .concat([
            {
              key: t('::AddWhat', { 0: t('::Watchlist').toLocaleLowerCase() }),
              value: 'add',
              icon: <PlusCircle color={'$success'} />
            }
          ]),
      []
    )
  });

  const onSelect = useCallback(
    (value: string) => {
      if (value === 'add') {
        return router.navigate('/(tabs)/watchlists/create');
      }

      router.setParams({ id: value });
    },
    [router]
  );

  if (isLoading) return <Spinner />;

  if (isSuccess) {
    if (isWeb) {
      return <Page.Heading>{children}</Page.Heading>;
    }

    return (
      portfolios && (
        <Select
          options={portfolios}
          value={selected}
          onChange={onSelect}
          isRequired
          headerTitle={t('::SelectWhat', { 0: t('::Watchlist').toLocaleLowerCase() })}
          trigger={
            <Row gap={'$1'} alignItems={'center'}>
              <Page.Heading>{children}</Page.Heading>
              <ChevronDown color={'$headingFont'} mt={'$1.5'} />
            </Row>
          }
        />
      )
    );
  }

  return null;
});

WatchlistHeaderTitle.displayName = 'WatchlistHeaderTitle';
